<template>
  <template v-if="canEditPolicy">
    <div class="w-full flex justify-between items-center mb-8">
      <div class="flex-1">
        <div v-if="!isEditing">
          <h1 class="font-bold text-left text-black text-lg">{{ policyName }}</h1>
        </div>
        <div v-else class="flex items-center gap-2">
          <input
            v-model="tempPolicyName"
            class="border border-gray-300 rounded-md px-2 py-1 w-full"
          />
        </div>
      </div>

      <div class="flex items-center gap-6 ml-4">
        <template v-if="!isEditing">
          <EditIcon color="#2f7f91" class="cursor-pointer w-8 h-8" @click="startEditing" />
        </template>
        <template v-else>
          <CheckIcon color="#2f7f91" class="cursor-pointer w-6 h-6" @click="saveEdit" />

          <CloseIcon color="#DA1E28" class="cursor-pointer w-6 h-6" @click="cancelEdit" />
        </template>
      </div>
    </div>

    <div class="w-full flex flex-col lg:flex-row justify-between items-start gap-4 mb-8">
      <div class="w-full lg:w-4/5">
        <SearchInput
          v-model="textInput"
          :search-function="onSearchByTabIndex"
          width="80%"
          :loading="loading"
          :label="`Buscar médicos`"
          hide-details
        />
      </div>

      <div class="w-full lg:w-1/5">
        <outlined-button
          @click="openFilterModal()"
          :text="'Filtrar'"
          :loading="loading"
          width="100%"
          height="56px"
          :prepend-icon="mdiFilter"
        />
      </div>
    </div>

    <ul
      class="sm:gap-14 gap-0 border-b flex sm:justify-start justify-between text-white select-none"
    >
      <li>
        <button
          class="w-max"
          @click="changeTab(1)"
          :class="tab === 1 ? activeClass : inactiveClass"
        >
          Autorizados
        </button>
      </li>

      <li>
        <button
          class="w-max"
          @click="changeTab(2)"
          :class="tab === 2 ? activeClass : inactiveClass"
        >
          Bloqueados
        </button>
      </li>

      <li>
        <button
          class="w-max"
          @click="changeTab(3)"
          :class="tab === 3 ? activeClass : inactiveClass"
        >
          Domínio
        </button>
      </li>
    </ul>

    <div class="flex flex-col gap-10 sm:h-[570px] justify-between">
      <div v-if="tab === 1">
        <Authorized
          :doctors="policyStore.authorizedDoctors"
          :loading="policyStore.isDoctorsLoading"
        />
      </div>
      <div v-if="tab === 2">
        <Blocked :doctors="policyStore.blockedDoctors" :loading="policyStore.isDoctorsLoading" />
      </div>
      <div v-if="tab === 3"><Domain /></div>
    </div>

    <Spinner
      v-if="loading"
      :color="'primary'"
      :size="'2rem'"
      :width="'0.5rem'"
      class="absolute inset-0 flex justify-center items-center bg-white bg-opacity-50 z-50"
    />

    <FilterModal
      v-if="filterModalProps"
      :show-authorization-status-filter="true"
      :modalStateProp="filterModalProps"
      @close="closeFilterModal"
      @apply-filters="applyFilters"
    />
  </template>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'

import { mdiFilter } from '@mdi/js'
import EditIcon from '@/assets/icons/edit.vue'
import CheckIcon from '@/assets/icons/check-icon.vue'
import CloseIcon from '@/assets/icons/close.vue'

import { useDsPolicyStore } from '@/pinia-store/useDsPolicyStore'

import SearchInput from '@/components/designSystem/Inputs/SearchInput.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilterModal from '../FilterDsPoliciesModal/FilterModal.vue'

import Authorized from './Authorized/authorized.tab.vue'
import Blocked from './Blocked/blocked.tab.vue'
import Domain from './Domain/index.vue'
import Spinner from '@/components/others/Spinner.vue'
import { useRoute, useRouter } from 'vue-router'
import PATHS from '@/router/paths'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'

const tab = ref(1)
const loading = ref(false)
const activeClass = ref('inline-block py-4 border-b-2 border-primary text-black font-semibold')
const inactiveClass = ref('inline-block py-4 text-gray-500')
const filterModalProps = ref(false)
const isEditing = ref(false)
const policyName = ref('Nome da Política')
const tempPolicyName = ref(policyName.value)

const policyStore = useDsPolicyStore()
const toast = useToast()
const { notifyError } = useErrorToast()
const route = useRoute()
const router = useRouter()
const policyId = ref(Number(route.params.ds_policy_id))
const textInput = ref(route.query.q ? String(route.query.q) : '')
const permissionsStore = usePermissionsStore()

const canEditPolicy = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.StationCreating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  })
})

onMounted(async () => {
  let policy = policyStore.currentPolicy

  textInput.value = route.query.q ? String(route.query.q) : ''

  if (!policy) {
    try {
      policy = await policyStore.fetchDsPolicyById(Number(route.params.ds_policy_id))
    } catch (e) {
      notifyError(e, 'Não foi possível carregar a política de restrição.')
      return
    }
  }

  if (policy) {
    policyName.value = policy.name
    policyId.value = policy.id
  } else {
    console.warn('Nenhuma política encontrada')
  }
})

const startEditing = () => {
  tempPolicyName.value = policyName.value
  isEditing.value = true
}

const saveEdit = async () => {
  const newName = tempPolicyName.value.trim()
  if (!newName || !policyStore.currentPolicy) return
  try {
    loading.value = true
    const updated = await policyStore.clpatchDsPolicy(
      policyStore.currentPolicy.id,
      policyStore.currentPolicy.companyOwner.id,
      {
        name: newName
      }
    )
    policyName.value = updated.name
    toast.success('Nome da política atualizado com sucesso!')
    isEditing.value = false
  } catch (e) {
    notifyError(e, 'Não foi possível atualizar o nome da política de restrição.')
  } finally {
    loading.value = false
  }
}

const cancelEdit = () => {
  tempPolicyName.value = policyName.value
  isEditing.value = false
}

const changeTab = async (newTab: number) => {
  if (tab.value !== newTab) {
    tab.value = newTab
    await handleTabChange()
  }
}

// Trata o próprio erro: tem 3 callers (troca de aba sem catch, busca e filtros) e a
// mensagem certa depende da aba ativa.
const handleTabChange = async () => {
  if (tab.value === 1) {
    try {
      await policyStore.fetchAuthorizedDoctors(policyId.value, {
        ...route.query
      })
    } catch (e) {
      notifyError(e, 'Não foi possível carregar os médicos autorizados da política.')
    }
  }

  if (tab.value === 2) {
    try {
      await policyStore.fetchBlockedDoctors(policyId.value, {
        ...route.query
      })
    } catch (e) {
      notifyError(e, 'Não foi possível carregar os médicos bloqueados da política.')
    }
  }
}

const onSearchByTabIndex = async () => {
  await router.push({
    path: `${PATHS.MANAGE_DOCTOR_AUTHORIZATION_POLICIES}/${policyId.value}`,
    query: { q: textInput.value, company_ids: route.query.company_ids || [] }
  })
  await handleTabChange()
}

const openFilterModal = () => {
  filterModalProps.value = true
}

const closeFilterModal = () => {
  filterModalProps.value = false
}

interface Company {
  title: string
  value: string
}

interface Filters {
  search: string
  companies: Company[]
  isActive?: string
  authorizationStatus?: string
}

const appliedFilters = ref<Filters>({
  search: '',
  companies: []
})

const applyFilters = async (filters: Filters) => {
  appliedFilters.value = filters

  const query: Record<string, any> = {
    q: route.query.q,
    company_ids: filters.companies.map((c) => c.value).join(',')
  }

  if (filters.authorizationStatus) query.authorizationStatus = filters.authorizationStatus
  if (filters.isActive) query.isActive = filters.isActive === 'active' ? true : false

  await router.push({
    path: `${PATHS.MANAGE_DOCTOR_AUTHORIZATION_POLICIES}/${policyId.value}`,
    query
  })

  try {
    loading.value = true
    // `handleTabChange` já notifica a falha de carga da aba.
    await handleTabChange()
  } finally {
    filterModalProps.value = false
    loading.value = false
  }
}
</script>

<style scoped></style>
